import { useMe } from "@/api/services/main/auth";
import { TMePasskeySignalDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { signalPasskeyUserDetails } from "@/features/auth/utils/passkey";
import { useEffect } from "react";

/**
 * Keeps the labels stored by the browser's passkey provider in sync with `/get-me`, so the passkey
 * picker stops showing the old address after メールアドレス変更.
 *
 * Mount once in the authenticated shell. That is deliberately the only call site: the メールアドレス変更
 * confirmation endpoint is unauthenticated and its link is usually opened in a different browser from
 * the one holding the passkey, so signalling there would miss. Syncing on every authenticated boot
 * instead converges on each browser the moment its user signs in again.
 *
 * Reuses the shared `USER.FETCH_ME` query — no extra request. Runs regardless of whether this browser
 * registered a passkey itself: passkeys sync across devices, so a browser can hold the credential
 * without the local hint, and signalling when the provider holds nothing is a silent no-op.
 */
export const usePasskeyUserDetailsSync = () => {
  const { data } = useMe();
  const signal = data?.data?.passkeySignal;
  // /get-me is polled on an interval and returns a fresh object each time — key the effect on the
  // values so it only re-runs when the labels actually change.
  const signalKey = signal ? JSON.stringify(signal) : "";

  useEffect(() => {
    if (!signalKey) return;
    void signalPasskeyUserDetails(JSON.parse(signalKey) as TMePasskeySignalDto);
  }, [signalKey]);
};
